import { PayloadAction, WithSlice, createSlice } from '@reduxjs/toolkit';
import update from 'immutability-helper';

import { state$ } from '../../streams';
import { SUPPORT_ROOM_KEY, GAME_ROOM_KEY } from '../constants/chat';
import { menuWidgetSelector } from '../selectors/menu';
import { GameLoadedRootState } from '../types';

export type MessageType = {
  timeStamp: number;
  timestamp: number;
  tableId: string | number;
  text: string;
  chat: string;
  type: string;
  name: string;
  uid: string;
  roomType: string;
  joined: boolean;
};

export type MessagePayload = MessageType | MessageType[];

export type SendMessagePayload = {
  chat: string;
  text: string;
}

export type ChatRoomType = typeof GAME_ROOM_KEY | typeof SUPPORT_ROOM_KEY;

export type SupportTicket = {
  id?: string;
  chatRoomName?: string;
  status?: string;
  reason?: string;
  success?: boolean;
  pending?: boolean;
  failed?: boolean;
  error?: boolean;
  errorMessage?: string;
  username?: string;
};

export type SupportError = string;

export type ChatCredentials = {
  username: string, password: string, nickname: string
}

export type ChatState = {
  tableId?: number;
  hasUserCredentials: boolean;
  mute: boolean;
  loading: boolean;
  error?: boolean;
  errorMessage?: string;
  [GAME_ROOM_KEY]?: { messages: MessageType[] },
  [SUPPORT_ROOM_KEY]?: { messages: MessageType[] },
  supportTicket?: SupportTicket
  credentials?: ChatCredentials
}

const initialState: ChatState = {
  hasUserCredentials: false,
  mute: false,
  loading: true,
  error: false,
  [GAME_ROOM_KEY]: { messages: [] },
  [SUPPORT_ROOM_KEY]: { messages: [] },
  supportTicket: {
    success: false,
    pending: false,
    failed: false,
  },
};

const widgetKeyMap = {
  [SUPPORT_ROOM_KEY]: 'liveSupport',
  [GAME_ROOM_KEY]: 'chat',
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    openSupportTicket: (state, { payload: reason }: PayloadAction<string>) => ({
      ...state,
      supportTicket: {
        ...state.supportTicket,
        pending: true,
        reason,
      },
    }),
    checkSupportTicket: () => {},
    setSupportTicketUsername: (state, { payload } : PayloadAction<string>) => ({
      ...state,
      supportTicket: {
        ...state.supportTicket,
        username: payload,
      },
    }),
    setSupportTicket: (state, { payload } : PayloadAction<SupportTicket>) => ({
      ...state,
      supportTicket: {
        ...state.supportTicket,
        ...payload,
      },
    }),
    toggleMute: (state) => ({
      ...state,
      mute: !state.mute,
    }),
    setMute: (state, { payload: mute } : PayloadAction<boolean>) => ({
      ...state,
      mute,
    }),
    setBoshConnectionStatus: (state, { payload: boshConnectionStatus }: PayloadAction<number>) => ({
      ...state,
      boshConnectionStatus,
    }),
    setCredentials: (state, { payload } : PayloadAction<ChatCredentials>) => ({
      ...state, credentials: payload, hasUserCredentials: true,
    }),
    gettingCredentialsFailed: (state) => ({ ...state, hasUserCredentials: false }),
    setRoster: () => {},
    joinRoomSucceeded: (state) => ({
      ...state,
      [GAME_ROOM_KEY]: { messages: [] },
      [SUPPORT_ROOM_KEY]: { messages: [] },
      loading: false,
    }),
    joinRoomFailed: (state, { payload: errorMessage }: PayloadAction<string>) => ({
      ...state,
      error: true,
      errorMessage,
      loading: false,
      mute: false,
    }),
    leaveRoom: (state) => ({
      ...state,
      mute: false,
      loading: false,
      error: false,
    }),
    sendMessage: (_state, _action: PayloadAction<SendMessagePayload>) => {},
    sendMesageFailed: (state, { payload: errorMessage }: PayloadAction<string>) => ({
      ...state,
      error: true,
      errorMessage,
    }),
    setMessage: (state, { payload } : PayloadAction<MessagePayload>) => {
      const stateSource: any = state$?.source;
      const menuWidget: Record<string, any> = menuWidgetSelector(stateSource?.value);
      if (Array.isArray(payload)) {
        const updates = payload.reduce((acc, m) => {
          const key = m.roomType;
          const widgetKey = widgetKeyMap[key as keyof typeof widgetKeyMap];

          acc[key] = acc[key] || {
            messages: {
              $push: [],
            },
          };
          acc[key].messages.$push.push({
            ...m,
            unread: !menuWidget.drawer || menuWidget.key !== widgetKey,
          });

          return acc;
        }, {} as any);

        return update(state, updates);
      }
      const widgetKey = widgetKeyMap[payload.roomType as keyof typeof widgetKeyMap];
      return update(state, {
        [payload.roomType]: {
          messages: { $push: [ { ...payload, unread: !menuWidget.drawer || menuWidget.key !== widgetKey } ] },
        },
      });
    },
    readMessage: (state, { payload: roomType }: PayloadAction<ChatRoomType>) => ({
      ...state,
      [roomType]: {
        ...state[roomType],
        messages: state[roomType]?.messages?.map?.((m: any) => ({ ...m, unread: false })),
      },
    }),
  },
});

declare module '..' {
  export interface LazyLoadedSlices extends WithSlice<typeof chatSlice> {}
}

export const {
  openSupportTicket,
  checkSupportTicket,
  setSupportTicketUsername,
  setSupportTicket,
  toggleMute,
  setMute,
  setBoshConnectionStatus,
  setRoster,
  joinRoomSucceeded,
  joinRoomFailed,
  leaveRoom,
  sendMessage,
  sendMesageFailed,
  setMessage,
  readMessage,
  setCredentials,
  gettingCredentialsFailed,
} = chatSlice.actions;

export default chatSlice.reducer;

export type RootStateWithChatLoaded = GameLoadedRootState & {
  chat: Required<ChatState>;
}
